import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { loadPageData } from 'redux/actions/Request';
import SimpleTable from 'views/Components/SimpleTable';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  FormControl,
  InputLabel,
  MenuItem 
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { makeStyles } from '@material-ui/core/styles';
import ActionDialog from 'views/Components/ActionDialog';

import Codex from '@blumira/blu_constants';

const useStyles = makeStyles((theme) => ({
  showRulesSelectTitle: {
    margin: 0,
    padding: 0
  },
  showRulesSelectItem: {
    marginTop: "20px !important",
    marginRight: 10,
    minWidth: 240,
    backgroundColor: theme.palette.background.paper
  },
  dialogHeaderContainer: {
      display: 'flex',
      paddingRight: 30,
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${theme.palette.divider}`
  },
  dialogContent: {
      padding: theme.shape.padding * 2,
  },
  close: {
      cursor: 'pointer'
  },
  dialogTable: {
    '& td': {
      paddingBottom: 15,
      verticalAlign: 'top'
    },
  },
  tableRowLabel: {
    fontWeight: 'bold',
    paddingRight: 20,
    whiteSpace: 'nowrap'
  },
  defaultStateDescrip: {
    display: 'block',
    marginTop: 10
  },
  priorityContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  priorityCircle: {
    width: 15,
    height: 15,
    marginRight: 5,
    borderRadius: '50%'
  },
}))

const isRuleEnabled = (rule) => rule.status===1 || rule.status===101 //1=enabled, 101=pending_enabled

const RulesView = (props) => {
  const classes = useStyles()

  const [rules, setRules] = useState([])
  const [showRulesDropdownVal, setShowRulesDropdownVal] = useState('enabled')
  const [open, setOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [selectedRule, setSelectedRule] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    if(props.rules?.length && props.conditions?.length && props.actions?.length && props.findingTypes?.length){
      let rulesPlus = []
      const thisOrgsRules = props.rules.filter(r=>r.orgId===props.orgId)
      const promises = thisOrgsRules.map(rule => {
        const thisCondition = props.conditions?.filter(c=>c.id === rule.conditionSet[0].condition)[0]
        rule.params.fullAnalysisSummary = thisCondition?.analysis
        const thisAction = props.actions?.filter(a=>a.id === thisCondition?.targetAction)[0]
        rule.params.priority = thisAction?.priority
        const thisCategory = props.findingTypes?.filter(t=>t.id === thisAction?.findingType)[0]
        rule.params.category = thisCategory?.name
        const theTags = thisCondition?.tags?.filter(t=>t.substring(0,5)==='type:')
        rule.params.dataType = theTags?.length && theTags[0]?.length ? props.language[theTags[0].replace("type:", "")]?.display_name : ''
        rule.params.defaultState = thisCondition?.tags?.some(t=>t==="status:default_disabled") ? 'Disabled' : 'Enabled'
        return rulesPlus.push(rule)
      })
      Promise.all(promises).then(() => {
        setRules(rulesPlus)
        filterRules(showRulesDropdownVal)
        setIsLoading(false)
      })
    }
    else if(props.ready && !props.rules.length) // loading finished and no rules
      setIsLoading(false)
  }, [props.ready, props.rules, props.conditions, props.actions, props.findingTypes]) /* eslint-disable-line */

  const replacePre = (html) => {
    return html.replaceAll('<pre>', '<code>').replaceAll('</pre>', '</code>')
  }

  const stripHTML = (html) => {
    let doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent || ""
  }

  const getAnalysis = (column, model, getFullSummary) => {
    const maxChars = 256
    if(model.fullAnalysisSummary) {
      if(getFullSummary) {
        return replacePre(model.fullAnalysisSummary)
      } else {
        const stripped = stripHTML(model.fullAnalysisSummary)
        return stripped.length > maxChars ? stripped.substring(0, maxChars).trim()+'...' : stripped
      }
    } else {
      return ''
    }
  }

  const getPriority = (column, model) => {
    const priority = rules.filter(r => r.id === model.id)[0]?.priority
    let color
    switch(priority) {
      case 1:
        color = 'red'
        break;
      case 2:
        color = '#FDE03A'
        break;
      case 3:
        color = 'gray'
        break;
      default:
        color = 'gray'
        break;
    }
    
    return (
      <span className={classes.priorityContainer}>
        <div className={classes.priorityCircle} style={{backgroundColor: color}} />
        P{priority}
      </span>
    )
  }

  const columns = [
    {
      title: 'Rule name',
      field: 'name',
      css: { width: '20%' },
      searchable: true
    },
    {
      title: 'Category',
      field: 'category',
      css: { width: '120px' },
      searchable: true,
    },
    {
      title: 'Priority',
      field: 'priority',
      css: { width: '120px' },
      searchable: false,
      renderValue: getPriority
    },
    {
      title: 'Data Type',
      field: 'dataType',
      css: { width: '160px' },
      searchable: true,
    },
    {
      title: 'Analysis summary',
      field: 'fullAnalysisSummary',
      searchable: true,
      renderValue: getAnalysis
    },
  ]

  const filterRules = (rulesFilter) => {
    setShowRulesDropdownVal(rulesFilter)
    let filteredRules
    switch(rulesFilter){
      case 'enabled':
        filteredRules = props.rules.filter(r => r.orgId === props.orgId && r.status===1)
        break
      case 'disabled':
        filteredRules = props.rules.filter(r => r.orgId === props.orgId && r.status!==1)
        break
      default:
        filteredRules = props.rules.filter(r => r.orgId === props.orgId)
        break
    }
    setRules(filteredRules)
  }

  const handleViewDetails = (contextMenu) => {
    //model = contextMenu.model if "View details" is clicked from the context menu
    //model = just contextMenu if triggering directly from row click,
    //which happens when/if current user does not have permissions to enable/disable rule and therefore, no context menu shows
    //since currently, the only 2 things in the menu are enable/disable or "View details"
    const model = contextMenu.model || contextMenu
    setSelectedRule(model)
    setOpen(true)
  }
  
  const handleClose = (action) => setOpen(false)

  const handleDisable = (theSelectedRule) => {
    theSelectedRule.set({ status: 0 })
    theSelectedRule.update()
    .then((obj)=>{
      obj.set({ status: 0 },false)
      setSelectedRule(obj);
    })
    
  }

  const handleEnable = (theSelectedRule) => {
    theSelectedRule.set({ status: 1 })
    theSelectedRule.update()
    .then((obj)=>{
      obj.set({ status: 1 },false);
      setSelectedRule(obj)
    })
  }

  const handleSwitchToggle = (obj) => {
    const rule = obj instanceof rules[0].constructor ? obj : obj.model;
    setSelectedRule(rule)
    setConfirmationOpen(true)
  }

  const handleConfirmation = () => {
    if(isRuleEnabled(selectedRule)) {
      handleDisable(selectedRule) 
    } else {
      handleEnable(selectedRule)
    }
    setConfirmationOpen(false)
  }

  return (
    <div>
      <ActionDialog
          open={confirmationOpen}
          title={
            `${isRuleEnabled(selectedRule) ? 'Confirm detection rule change' : 'Are you sure?'}`
          }
          description={
            isRuleEnabled(selectedRule) ? //confirm disable
              <span>
                  Are you sure you want to disable this detection rule?
              </span>
            :  //else confirm enable
              (selectedRule.defaultState==='Disabled') ?
                <span>
                    Blumira's incident detection team has disabled this detection rule 
                    by default because it is likely to produce noisy findings.
                </span>
              :
                <span>
                  Are you sure you want to enable this detection rule?
                </span>
          }
          actions = {[
            {
            'title': 'Cancel',
            'action': () => setConfirmationOpen(false)
            },
            {
            'title': `Yes, ${isRuleEnabled(selectedRule) ? 'disable' : 'enable'} it`,
            'action': handleConfirmation,
            'variant': 'contained',
            'datacy': 'rulesConfirmChangeBtn'
            },
          ]}
      />
      <Dialog open={open} onClose={handleClose} maxWidth={'md'}>
          <div className={classes.dialogHeaderContainer}>
            <DialogTitle>Detection rule details</DialogTitle>
            <CloseIcon className={classes.close} onClick={handleClose} datacy="rulesDialogCloseBtn" />
          </div>
          <DialogContent className={classes.dialogContent}>
            <table className={classes.dialogTable}>
              <tbody>
                <tr>
                  <td className={classes.tableRowLabel}>Name</td>
                  <td>{selectedRule.name}</td>
                </tr>
                {selectedRule.dataType !== '' &&
                    <tr>
                      <td className={classes.tableRowLabel}>Data type</td>
                      <td>{selectedRule.dataType}</td>
                    </tr>
                }
                <tr>
                  <td className={classes.tableRowLabel}>Category</td>
                  <td>{selectedRule.category}</td>
                </tr>
                <tr>
                  <td className={classes.tableRowLabel}>Priority</td>
                  <td>{getPriority("priority", selectedRule)}</td>
                </tr>
                <tr>
                  <td className={classes.tableRowLabel}>Analysis summary</td>
                  <td dangerouslySetInnerHTML={{__html: getAnalysis("summary", selectedRule, true)}} />
                </tr>
                <tr>
                  <td className={classes.tableRowLabel}>Current state</td>
                  <td>
                      {isRuleEnabled(selectedRule) ? 'Enabled' : 'Disabled'}
                  </td>
                </tr>
                <tr>
                  <td className={classes.tableRowLabel}>Default state</td>
                  <td>
                      {selectedRule.defaultState}
                      <span className={classes.defaultStateDescrip}>
                        {
                          (selectedRule.defaultState==='Enabled') ?
                            "Blumira's incident detection team has determined this rule generates findings that are likely to present a threat so it is enabled by default."
                          :
                            "Blumira's incident detection team has determined this rule generates noisy findings that are not likely to present threats so it is disabled by default."
                        }
                      </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </DialogContent>
      </Dialog>
      <SimpleTable
        isNorthStar
        title="Rules"
        isFetching={isLoading}
        columns={columns}
        data={rules}
        resultsCount={0}
        rowToggleSwitches={props}
        handleSwitchToggle={handleSwitchToggle}
        handleViewDetails={handleViewDetails}
        emptyText={<span>Nothing to see here.<br /><br />NOTE: Detection rules will appear as soon as Blumira processes your incoming log data. This can take up to 60 minutes after you set up your first Cloud Connector or Sensor.</span>}
        contextMenuItems={[
          {onClick: handleViewDetails, text: 'View details', datacy: 'rulesContextMenuItemViewDetails'},
          {onClick: handleSwitchToggle, text: 'Enable/Disable', datacy: 'rulesContextMenuItemEnableDisable', detectionRule: true}
        ]}
        toolbarActions={[
          {
            isPrimary: true,
            component: (
              <FormControl className={classes.showRulesFormControl}>
                <InputLabel id="rules-select-label" className={classes.showRulesSelectTitle}>Search preset</InputLabel>
                <Select
                  labelId="rules-select-label"
                  id="rules-select"
                  value={showRulesDropdownVal}
                  onChange={(event) => filterRules(event.target.value)}
                  className={classes.showRulesSelectItem}
                >
                  <MenuItem value="all">All detection rules</MenuItem>
                  <MenuItem value="enabled">Enabled</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </Select>
              </FormControl>
            )
          }
        ]}
      />
    </div>
  )
}

const mapDispatchToProps = dispatch => {
  return {
    reload: (force) => dispatch(loadPageData(force))
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    user: state.session.settings.user,
    orgId: state.page.payload.orgId,
    language: Codex.language.models.bq.type
  }
}

RulesView.defaultProps = {
  ready: false,
  rules: [],
  conditions: [],
  actions: [],
  findingTypes: [],
}

export default connect(mapStateToProps, mapDispatchToProps)(RulesView)